import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { BellRing, Mail, MessageCircle, Copy, Loader2, ExternalLink } from "lucide-react";
import api, { errMsg } from "@/lib/api";
import { fdate, money } from "@/lib/fmt";
import { PageHeader, Empty } from "@/components/Common";
import { SalesFilter } from "@/components/SalesFilter";
import { Button } from "@/components/ui/button";

const msg = (r) => [
  `Yth. Bapak/Ibu ${r.pic_name || ""}`.trim() + `,`, r.customer_name, "",
  `Kami ingin mengingatkan bahwa penawaran kami No. ${r.display_no}${r.project_title ? ` (${r.project_title})` : ""} berlaku sampai ${fdate(r.valid_until)}.`,
  `Nilai penawaran: ${money(r.offer_total, r.currency)}${r.ppn_enabled ? " (incl. PPN)" : ""}.`, "",
  "Mohon konfirmasinya agar kami dapat segera memproses lebih lanjut. Terima kasih.", "", `Hormat kami,`, r.sales_name,
].join("\n");

const waLink = (r) => {
  let p = (r.pic_phone || "").replace(/\D/g, "");
  if (p.startsWith("0")) p = `62${p.slice(1)}`;
  return `https://wa.me/${p}?text=${encodeURIComponent(msg(r))}`;
};

const Item = ({ label, children }) => (
  <div><p className="text-[11px] font-semibold uppercase tracking-wide text-slate-400">{label}</p><div className="mt-0.5 text-sm text-slate-800">{children}</div></div>
);

const Card = ({ r, i, onSent }) => {
  const nav = useNavigate();
  const [busy, setBusy] = useState(false);
  const sendMail = async () => {
    if (!r.email) { toast.error("Email PIC customer belum diisi di menu Customer"); return; }
    if (!window.confirm(`Kirim email pengingat + PDF penawaran ke ${r.email}?`)) return;
    setBusy(true);
    try { await api.post(`/quotations/${r.id}/send-email`, null, { params: { reminder: true } }); toast.success(`Pengingat terkirim ke ${r.email}`); onSent(); } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const urgent = r.days_left <= 1;
  return (
    <div className="card-premium p-6 fade-up" data-testid={`reminder-card-${i}`}>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <p className="font-heading text-lg font-semibold text-slate-900" data-testid={`reminder-customer-${i}`}>{r.customer_name}</p>
          <p className="font-num text-sm font-semibold text-blue-800">{r.display_no} <span className="ml-1 rounded bg-rose-50 px-1.5 py-0.5 font-sans text-[11px] text-rose-700">{r.version_label}</span></p>
          {r.project_title && <p className="text-xs text-slate-500">{r.project_title}</p>}
        </div>
        <span className={`rounded-full px-3 py-1 text-xs font-bold ${urgent ? "bg-rose-100 text-rose-700" : "bg-amber-100 text-amber-800"}`} data-testid={`reminder-days-${i}`}>{r.valid_until < new Date().toISOString() ? "Sudah habis" : r.days_left <= 0 ? "Berakhir hari ini" : `${r.days_left} hari lagi`}</span>
      </div>
      <div className="mt-4 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <Item label="Tanggal">{fdate(r.created_at)}</Item>
        <Item label="Berlaku s/d">{fdate(r.valid_until)}</Item>
        <Item label="PIC">{r.pic_name || "-"}{r.pic_phone && <span className="block text-xs text-slate-500">{r.pic_phone}</span>}</Item>
        <Item label="Sales">{r.sales_name}</Item>
        <div className="sm:col-span-2"><Item label="Alamat">{r.customer_address || "-"}</Item></div>
        <div className="sm:col-span-2">
          <Item label={`Nilai Penawaran${r.ppn_enabled ? " (incl. PPN)" : ""}`}>
            {r.before_nego_total != null ? (
              <span data-testid={`reminder-value-${i}`}><span className="font-num text-slate-400 line-through">{money(r.before_nego_total, r.currency)}</span>
                <span className="ml-2 font-num text-lg font-bold text-emerald-700">{money(r.offer_total, r.currency)}</span>
                <span className="ml-2 rounded bg-emerald-100 px-1.5 py-0.5 text-[11px] font-bold text-emerald-800">Setelah Nego</span></span>
            ) : <span className="font-num text-lg font-bold text-blue-900" data-testid={`reminder-value-${i}`}>{money(r.offer_total, r.currency)}</span>}
          </Item>
        </div>
      </div>
      {r.revision_notes.map((n, j) => (
        <p key={j} className="mt-3 rounded-lg bg-rose-50 px-4 py-2.5 text-sm text-rose-900" data-testid={`reminder-revnote-${i}-${j}`}><b>Catatan Revisi ({n.label}):</b> {n.note}</p>
      ))}
      <div className="mt-4 flex flex-wrap items-center gap-2 border-t border-slate-100 pt-4">
        <Button size="sm" onClick={sendMail} disabled={busy} data-testid={`reminder-email-${i}`}>{busy ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Mail className="h-4 w-4 mr-1" />}Email + PDF</Button>
        <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700" disabled={!r.pic_phone} title={r.pic_phone ? "" : "No. HP PIC belum diisi di menu Customer"} onClick={() => window.open(waLink(r), "_blank")} data-testid={`reminder-wa-${i}`}><MessageCircle className="h-4 w-4 mr-1" />WhatsApp</Button>
        <Button size="sm" variant="outline" className="bg-white" onClick={() => navigator.clipboard.writeText(msg(r)).then(() => toast.success("Pesan disalin"))} data-testid={`reminder-copy-${i}`}><Copy className="h-4 w-4 mr-1" />Copy Pesan</Button>
        <Button size="sm" variant="ghost" onClick={() => nav(`/quotations/${r.id}`)} data-testid={`reminder-open-${i}`}><ExternalLink className="h-4 w-4 mr-1" />Buka Penawaran</Button>
        {r.last_reminder_at && <span className="ml-auto text-xs text-slate-500">Terakhir diingatkan: {fdate(r.last_reminder_at)}</span>}
      </div>
    </div>
  );
};

export default function Reminders() {
  const [days, setDays] = useState(0);
  const [sales, setSales] = useState("");
  const [rows, setRows] = useState(null);
  const load = () => api.get("/quote-reminders", { params: { days, sales_id: sales } }).then((r) => setRows(r.data)).catch((e) => toast.error(errMsg(e)));
  useEffect(() => { load(); }, [days, sales]); // eslint-disable-line react-hooks/exhaustive-deps
  return (
    <div data-testid="reminders-page">
      <PageHeader title="Reminder Penawaran" subtitle="Penawaran aktif yang masa berlakunya hampir habis. Kirim pengingat ke customer via Email (PDF terlampir) atau WhatsApp.">
        <select className="h-10 rounded-md border border-input bg-white px-3 text-sm" value={days} onChange={(e) => setDays(Number(e.target.value))} data-testid="reminders-days-select">
          <option value={0}>Semua akan habis</option>
          {[1, 3, 7, 14, 30].map((d) => <option key={d} value={d}>Habis ≤ {d} hari</option>)}
          <option value={-1}>Sudah habis</option>
        </select>
        <SalesFilter value={sales} onChange={setSales} />
      </PageHeader>
      {rows === null ? <div className="p-8 text-slate-400">Memuat…</div> : (
        <div className="space-y-4">
          <p className="flex items-center gap-2 text-sm text-slate-600"><BellRing className="h-4 w-4 text-amber-600" /><b data-testid="reminders-count">{rows.length}</b> penawaran {days < 0 ? "sudah habis masa berlakunya" : days === 0 ? "aktif, diurutkan dari yang paling dekat habis" : `akan habis dalam ${days} hari`}</p>
          {rows.map((r, i) => <Card key={r.id} r={r} i={i} onSent={load} />)}
          {!rows.length && <Empty />}
        </div>
      )}
    </div>
  );
}
